<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useVModel, syncRef, useScrollLock } from '@vueuse/core';
import { useEscapeStack } from '/app/composables/escape-stack';

defineOptions({ inheritAttrs: false });

const props = defineProps({
	modelValue: Boolean,
	closeOnBackdrop: {
		type: Boolean,
		default: true,
	},
	// Blocks user-initiated close (Escape + backdrop) while true. Programmatic
	// close via v-model still works. Hosts set this to guard e.g. in-flight uploads.
	preventClose: {
		type: Boolean,
		default: false,
	},
	// Per-class overrides for the content transition, merged over the defaults
	// below. Hosts that want the panel to come from somewhere other than the
	// middle of the screen - the Game Dash grows out of the navbar bell - swap
	// only the classes they care about and keep the rest.
	contentTransition: {
		type: Object,
		default: () => ({}),
	},
});

const CONTENT_TRANSITION = {
	enterActiveClass: 'transition-all transition-fast ease-out-quad',
	leaveActiveClass: 'transition-all transition-medium ease-in-quad',
	enterFromClass: 'opacity-0 scale-70',
	enterToClass: 'opacity-100 scale-100',
	leaveFromClass: 'opacity-100 scale-100',
	leaveToClass: 'opacity-0 scale-70',
};

const contentClasses = computed(() => ({ ...CONTENT_TRANSITION, ...props.contentTransition }));

const emit = defineEmits(['close', 'show', 'update:modelValue']);

const state = useVModel(props, 'modelValue', emit);
const showModal = ref(state.value);
const showBackdrop = ref(false);
const showContent = ref(false);
const backdropLeaving = ref(false);
const contentLeaving = ref(false);
const leaving = computed(() => backdropLeaving.value || contentLeaving.value);
// The wrapper outlives its children while they leave; without this it would keep
// swallowing clicks on the page underneath for the whole transition.
const dismissing = computed(() => !showBackdrop.value && !showContent.value);
const payload = ref({});
const scrollLocked = useScrollLock(document.body, showModal.value);

// Backstop for the teardown below. Longer than the slowest leave transition in
// the app - the Game Dash collapsing into the navbar bell, 640ms of travel plus
// fade - so it only ever fires when nothing ran at all and the wrapper would be
// stranded.
const LEAVE_TIMEOUT_MS = 900;

let teardownTimer = null;

const teardown = () => {
	clearTimeout(teardownTimer);
	teardownTimer = null;
	showModal.value = false;
};

const open = (data = {}) => {
	clearTimeout(teardownTimer);
	teardownTimer = null;
	payload.value = data;
	showModal.value = true;
	showBackdrop.value = true;
	showContent.value = true;
	emit('show');
};

const close = () => {
	showBackdrop.value = false;
	showContent.value = false;

	// The wrapper has to outlive its children: dropping it here takes the
	// leaving nodes with it, which is why the leave transitions written in this
	// component never used to play. The `leaving` watcher removes it once both
	// have finished; the timer is the backstop for when neither runs.
	//
	// Skipped for a modal that was never open - on mount, and on any close of
	// an already-closed one - so those cost nothing but still announce `close`
	// on the tick they always did.
	if (showModal.value) {
		clearTimeout(teardownTimer);
		teardownTimer = setTimeout(teardown, LEAVE_TIMEOUT_MS);
	}

	emit('close');
};

syncRef(showModal, state, { direction: 'ltr' });
syncRef(showModal, scrollLocked, { direction: 'ltr' });

watch(
	() => props.modelValue,
	() => {
		props.modelValue ? open(payload.value) : close();
	},
	{ immediate: true }
);

watch(leaving, (active) => {
	if (!active) {
		teardown();
	}
});

useEscapeStack(showModal, () => {
	if (!props.preventClose) close();
});

onBeforeUnmount(() => {
	clearTimeout(teardownTimer);
});

defineExpose({
	open,
	close,
});
</script>

<template>
	<slot
		name="activator"
		v-bind="{ on: { click: open }, close, open }"
	/>
	<Teleport to="#modals">
		<div
			v-if="showModal"
			class="fixed inset-0 flex items-center justify-center z-[99999]"
			:class="{ 'pointer-events-none': dismissing }"
		>
			<Transition
				@before-leave="backdropLeaving = true"
				@after-leave="backdropLeaving = false"
				enter-active-class="transition-all transition-fast ease-out-quad"
				leave-active-class="transition-all transition-medium ease-in-quad"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
				appear
			>
				<div
					v-if="showBackdrop"
					class="absolute inset-0 bg-black bg-opacity-40"
					@click="closeOnBackdrop && !preventClose && close()"
				/>
			</Transition>
			<Transition
				@before-leave="contentLeaving = true"
				@after-leave="contentLeaving = false"
				:enter-active-class="contentClasses.enterActiveClass"
				:leave-active-class="contentClasses.leaveActiveClass"
				:enter-from-class="contentClasses.enterFromClass"
				:enter-to-class="contentClasses.enterToClass"
				:leave-from-class="contentClasses.leaveFromClass"
				:leave-to-class="contentClasses.leaveToClass"
				appear
			>
				<div
					v-if="showContent"
					class="relative"
					v-bind="$attrs"
				>
					<slot
						v-bind="payload"
						:visible="showModal"
						:close="close"
					></slot>
				</div>
			</Transition>
		</div>
	</Teleport>
</template>
